<script>
// Import general mixin for shared functionality.
import mixin from '../../mixin';

// Import primevue components
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

// Import JS Excel library
import * as XLSX from 'xlsx';

export default {
	mixins: [mixin],
	components: {
		DataTable,
		Column,
	},
	props: {
		data: {
			type: Array,
			default: [],
		},
		columns: {
			type: Array,
			default: [],
		},
		exportable: {
			type: Boolean,
			default: true,
		},
		sortable: {
			type: Boolean,
			default: true,
		},
		freezable: {
			type: Boolean,
			default: true,
		},
		scrollable: {
			type: Boolean,
			default: true,
		},
		textManipulation: {
			type: Boolean,
			default: false,
		},
		hoverable: {
			type: Boolean,
			default: false,
		},
		exportFilename: {
			type: String,
			default: 'export',
		},
		altStyle: {
			type: Boolean,
			default: false,
		},
		numbered: {
			type: Boolean,
			default: false,
		},
        autoLayout: {
            type: Boolean,
            default: false,
        },
	},
	data() {
		return {
			editedColumns: [...this.columns],
			sortObj: {
				order: 0,
				field: '',
			},
		};
	},
	computed: {
		computedColumns() {
			return this.editedColumns;
		},
		scrollHeight() {
			if (!this.scrollable) return null;
			if (this.data.length < 10) return this.data.length * 50 + 50 + 'px';
			return '500px';
		},
		getTableClasses() {
			let fixedClass = this.scrollable ? '' : 'is-fixed-layout';
			let styleClass = this.altStyle ? 'table--alternative' : '';
			let hoverableClass = this.hoverable ? 'is-hoverable' : '';
			let autoLayoutClass = this.autoLayout ? 'is-auto-layout' : '';
            return `table table--small table--datatable is-bordered is-fullwidth ${hoverableClass} ${fixedClass} ${styleClass} ${autoLayoutClass}`;
		},
		getColumnStyle() {
			let columnWidth = this.scrollable
				? 'initial'
				: 100 / this.computedColumns.length + '%';
			return {
				width: columnWidth,
			};
		},
	},
	methods: {
		exportCSV() {
			this.$refs.dt.exportCSV();
		},
		exportExcel() {
			const data = this.data;
			const columns = this.computedColumns;
			const customHeaders = this.computedColumns.map((col) => col.label);
			const excelData = data.map((row) => {
				return columns.map((col) => {
					const value = row[col.field];

					if (col.type === 'currency') {
						return this.nullCheck(this.toEuro(value, true));
					} else if (col.type === 'percentage') {
						return this.nullCheck(this.toPercentage(value));
					} else if (col.type === 'date') {
						return this.nullCheck(this.toDateStr(value));
					} else if (col.type === 'nullableDate') {
						return this.nullableDateCheck(this.toDateStr(value));
					} else {
						return this.nullCheck(value);
					}
				});
			});
			excelData.unshift(customHeaders);
			const worksheet = XLSX.utils.aoa_to_sheet(excelData);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Blad 1');
			XLSX.writeFile(workbook, `${this.exportFilename}.xlsx`);
		},
		freezeColumn(event, selectedColumn) {
			event.preventDefault();
			let column = this.editedColumns.find(
				(column) => column.label === selectedColumn.label
			);
			column.frozen = !column.frozen;
		},
		textManipulateColumn(event, selectedColumn) {
			event.preventDefault();
			let column = this.editedColumns.find(
				(column) => column.label === selectedColumn.label
			);
			column.textTruncated = !column.textTruncated;
		},
		nullCheck(value) {
			if (!value) return '-';
			return value;
		},
		nullableDateCheck(value) {
			if (!value || value === '1-1-1000' || value === '1-1-1') return '-';
			return value;
		},
		sort(event) {
			this.sortObj = {
				order: event.sortOrder,
				field: event.sortField,
			};
		},
		openDropdown() {
			this.$refs.dropdown.classList.toggle('is-active');
			document.addEventListener('click', (event) => {
				if (
					this.$refs.dropdown &&
					!this.$refs.dropdown.contains(event.target)
				) {
					this.$refs.dropdown.classList.remove('is-active');
				}
			});
		},
	},
};
</script>

<template>
	<template v-if="data.length">
		<DataTable
			ref="dt"
			:scrollable="scrollable"
			:scrollHeight="scrollHeight"
			:virtualScrollerOptions="{ itemSize: 30 }"
			:value="data"
			:pt="{
				table: getTableClasses,
			}"
			:exportFilename="exportFilename"
			@sort="sort"
		>
			<template v-if="exportable" #header>
				<div class="dropdown mb-2" ref="dropdown">
					<div class="dropdown-trigger has-background-white" @click="openDropdown">
						<button class="button is-tertiary is-outlined" aria-haspopup="true" aria-controls="dropdown-menu">
							<span class="icon">
								<i class="fa-solid fa-file-export"></i>
							</span>
							<span>{{ $store.getters.getRightText('dossierdetailLabelExport') }}</span>
							<span class="icon">
								<i class="fa-solid fa-angle-down" aria-hidden="true"></i>
							</span>
						</button>
					</div>
					<div class="dropdown-menu" id="dropdown-menu" role="menu">
						<div class="dropdown-content">
							<a @click="exportCSV" class="dropdown-item">
								<span class="icon"><i class="fa-solid fa-file-csv"></i></span>
								<span>{{ $store.getters.getRightText('dossierdetailLabelCsv') }}</span>
							</a>
							<a @click="exportExcel" class="dropdown-item">
								<span class="icon"><i class="fa-solid fa-file-excel"></i></span>
								<span>{{ $store.getters.getRightText('dossierdetailLabelExcel') }}</span>
							</a>
						</div>
					</div>
				</div>
			</template>
			<Column v-if="numbered" :header="$store.getters.getRightText('dossierdetailTableHeadPaymentsNumber')" :frozen="true">
				<template #body="slotProps">
					{{ slotProps.index + 1 }}
				</template>
			</Column>
			<Column
				v-for="column of computedColumns"
				:key="column.field"
				:field="column.field"
				:header="column.label"
				:sortable="sortable"
				:frozen="column.frozen"
				:style="getColumnStyle"
			>
				<template v-if="freezable" #header>
					<span class="icon" @click="freezeColumn($event, column)">
						<i :class="['fa-solid', 'fa-sm', column.frozen ? 'fa-lock' : 'fa-lock-open']"></i>
					</span>
				</template>
				<template v-if="textManipulation" #header>
					<span class="icon is-clickable" @click="textManipulateColumn($event, column)">
						<i :class="['fa-solid', 'fa-sm', column.textTruncated ? 'fa-eye-slash' : 'fa-eye']"></i>
					</span>
				</template>
				<template v-if="sortable" #sorticon="slotProps">
					<span :class="['icon', { 'soft-hide': !slotProps.sorted }]">
						<i :class="[ 'fa-solid', 'fa-sm', slotProps.sorted && slotProps.sortOrder === 1 ? 'fa-caret-up' : 'fa-caret-down']"></i>
					</span>
				</template>
				<template v-if="column.type === 'currency'" #body="slotProps">
					<p class="has-text-right">
						{{ nullCheck(toEuro(slotProps.data[column.field], true)) }}
					</p>
				</template>
				<template v-if="column.type === 'percentage'" #body="slotProps">
					<p class="has-text-right">
						{{ nullCheck(toPercentage(slotProps.data[column.field])) }}
					</p>
				</template>
				<template v-if="column.type === 'date'" #body="slotProps">
					<p :class="[column.textTruncated ? 'has-ellipsis' : 'is-wrap']">
						{{ nullCheck(toDateStr(slotProps.data[column.field])) }}
					</p>
				</template>
				<template v-if="column.type === 'nullableDate'" #body="slotProps">
					<p :class="[column.textTruncated ? 'has-ellipsis' : 'is-wrap']">
						{{ nullableDateCheck(toDateStr(slotProps.data[column.field])) }}
					</p>
				</template>
				<template v-if="column.type === 'dateText'" #body="slotProps">
					<p :class="[column.textTruncated ? 'has-ellipsis' : 'is-wrap']">
						{{ nullCheck(toDateStrShortMnth(slotProps.data[column.field])) }}
					</p>
				</template>
				<template v-if="column.type === 'amount'" #body="slotProps">
					<p class="has-text-right">
						{{ nullCheck(slotProps.data[column.field]) }}
					</p>
				</template>
				<template #body="slotProps">
					<p :class="[column.textTruncated ? 'has-ellipsis' : 'is-wrap']">
						{{ nullCheck(parseDossierNr(slotProps.data[column.field])) }}
					</p>
				</template>
			</Column>
		</DataTable>
	</template>
	<template v-else>
		<div class="box">
			{{ $store.getters.getRightText('dossierdetailLabelNoResults') }}
		</div>
	</template>
</template>
